import { Extension } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { isValidYoutubeUrl } from '@tiptap/extension-youtube';

/** True when the trimmed text is a single YouTube URL (watch or youtu.be). */
export function isYoutubeUrl(text: string): boolean {
  const t = text.trim();
  // Must be a bare URL (no surrounding prose) and a valid YouTube link.
  if (/\s/.test(t)) return false;
  // isValidYoutubeUrl returns a regex match (truthy) or null — coerce to bool.
  return Boolean(isValidYoutubeUrl(t));
}

const youtubePasteKey = new PluginKey('youtubePaste');

/**
 * Auto-embed: when a bare YouTube URL is pasted, convert it into a Youtube
 * embed node (via the @tiptap/extension-youtube `setYoutubeVideo` command)
 * instead of a plain text link. Defers (returns false) for anything else, so
 * normal paste — including MarkdownPaste — is unaffected.
 *
 * Requires the `Youtube` extension to be registered.
 */
export const YoutubePaste = Extension.create({
  name: 'youtubePaste',
  // Run before MarkdownPaste/default so a bare YouTube URL embeds rather than
  // being inserted as link text.
  priority: 1000,

  addProseMirrorPlugins() {
    const editor = this.editor;
    return [
      new Plugin({
        key: youtubePasteKey,
        props: {
          handlePaste(_view: EditorView, event: ClipboardEvent) {
            const text = event.clipboardData?.getData('text/plain') ?? '';
            if (!isYoutubeUrl(text)) return false;
            editor.chain().focus().setYoutubeVideo({ src: text.trim() }).run();
            return true;
          },
        },
      }),
    ];
  },
});
